<template>
    <div class="text-center mb-5">
        <h1 class="d-none mt-3 mb-5">Guess the Movie</h1>
        <h2 class="d-none">A simple game to try and guess the movie title from the movie quote</h2>
        <h3 class="d-none">Can you identify classic films just from a simple quote or line of text from the movie?</h3>

        <!-- QUOTE -->
        <transition name="fade" mode="out-in">
            <div
                class="mb-5"
                style="z-index: 2;position:relative;"
                :key="`quote-${selectedQuoteIndex}`"
            >
                <QuoteBubble
                    v-if="selectedQuote"
                    :selected-quote="selectedQuote"
                />
            </div>
        </transition>

        <v-card
            class="mx-auto mt-5 mb-10"
            max-width="500"
            color="white"
            elevation="3"
            rounded="lg"
        >
            <!-- AUTOCOMPLETE -->
            <v-card-text class="d-inline-flex align-center text-left pb-0">
                <v-autocomplete
                    ref="guessAutocomplete"
                    v-model="guess"
                    :items="titleSet"
                    :disabled="result !== null"
                    :menu-props="{maxHeight: 150}"
                    label="Guess the Movie Title..."
                    color="green darken-2"
                    clearable
                    @keydown.enter="submitAnswer"
                />
                <v-btn
                    v-if="!result"
                    :dark="Boolean(guess)"
                    :disabled="Boolean(!guess)"
                    color="green darken-1"
                    class="ml-3"
                    @click="submitAnswer"
                >
                    Submit <v-icon right>mdi-check-circle-outline</v-icon>
                </v-btn>
            </v-card-text>

            <!-- HINTS -->
            <v-card-text class="mb-3 pt-0 pl-2 pb-7">
                <div><v-btn text x-small color="primary" @click.prevent="showHints = !showHints">Need a hint?</v-btn></div>
                <transition name="fade">
                    <v-card
                        v-if="showHints"
                        class="text-left mt-3 mx-auto"
                        max-width="320"
                        color="blue lighten-5"
                    >
                        <v-card-title>Hints</v-card-title>
                        <v-card-subtitle>
                            Click <v-icon color="blue" small>mdi-eye</v-icon> to reveal the Movie Year or Actor who said the quote
                        </v-card-subtitle>
                        <v-divider />
                        <v-card-text>
                            <div>
                                <transition name="fade" mode="out-in">
                                    <div v-if="hintYear" key="display-movie-year-as-hintYear" class="mb-3">
                                        <div>Year: <v-chip color="primary lighten-1" dark small>{{ hintYear }}</v-chip></div>
                                    </div>
                                    <div v-else key="hintYear-button" class="mb-3">
                                        <v-btn
                                            text
                                            small
                                            color="primary"
                                            title="Click to give me the hint!"
                                            @click="getHintYear"
                                        >
                                            <v-icon class="mr-1">mdi-eye</v-icon> Year
                                        </v-btn>
                                    </div>
                                </transition>
                                <transition name="fade" mode="out-in">
                                    <div v-if="hintActor" key="display-movie-year-as-hintActor">
                                        <div>Actor: <v-chip color="primary lighten-1" dark small>{{ hintActor }}</v-chip></div>
                                    </div>
                                    <div v-else key="hintYear-button">
                                        <v-btn
                                            text
                                            small
                                            color="primary"
                                            title="Click to give me the hint!"
                                            @click="getHintActor"
                                        >
                                            <v-icon class="mr-1">mdi-eye</v-icon> Actor
                                        </v-btn>
                                    </div>
                                </transition>
                            </div>
                        </v-card-text>
                    </v-card>
                </transition>
            </v-card-text>

            <!-- QUOTE INFO -->
            <v-card-text class="mt-3">
                <small>
                    <span style="opacity:.75">Quote #{{ selectedQuoteNum }} of {{ quoteSet.length }}</span>
                    <span class="mx-3"></span>
                    <span
                        v-if="difficulty && difficultyObject"
                        :key="`difficulty-quote-${selectedQuoteIndex}`"
                    >
                        <v-icon
                            :color="difficultyObject.color"
                            small
                        >
                            mdi-{{ difficultyObject.icon }}
                        </v-icon>
                        <span class="ml-2" style="opacity:.75;">{{ difficulty }} Difficulty</span>
                    </span>
                </small>
            </v-card-text>

            <!-- ACTION -->
            <v-sheet color="grey lighten-4" class="d-flex align-center justify-space-between pa-3">
                <v-spacer />
                <v-btn @click="randomizeSelectedQuote">
                    New Quote <v-icon right>mdi-refresh</v-icon>
                </v-btn>
                <v-spacer />
            </v-sheet>
        </v-card>

        <!-- DIALOG POPUP -->
        <DialogMovieAnswer
            v-if="showDialogMovieAnswer"
            :correct-answer="result"
            :guess="guess"
            :movieImgSrc="movieImgSrc"
            :title="title"
            :hintYear="hintYear"
            :hintActor="hintActor"
            :difficulty="difficulty"
            :difficulty-object="difficultyObject"
            :imdbId="imdbId"
            :selectedQuoteIndex="selectedQuoteIndex"
            @close-modal="closeModal"
        />
    </div>
</template>

<script>
    import DialogMovieAnswer from '../components/DialogMovieAnswer';
    import QuoteBubble from '../components/QuoteBubble';

    export default {
        components: {
            DialogMovieAnswer,
            QuoteBubble,
        },
        data() {
            return {
                selectedQuoteIndex: null,
                hintYear: null,
                hintActor: null,
                showHints: false,
                title: null,
                guess: null,
                result: null,
                imdbId: null,
                movieData: null,
                movieImgSrc: null,

                showDialogMovieAnswer: false,
            };
        },

        created() {
            this.randomizeSelectedQuote();
        },

        computed: {
            titleSet() {
                return this.$store.getters.titleSet; // .sort() will sort the list, but then it affects the chosen answer comparison with "this.guess"
            },
            quoteSet() {
                return this.$store.getters.quoteSet;
            },
            selectedQuote() {
                let quote;
                if (!!this.quoteSet[this.selectedQuoteIndex]) {
                    quote = this.quoteSet[this.selectedQuoteIndex];
                    let quoteToHtml = `<div>${quote.replace(new RegExp('\n', 'g'), '<br><br>')}</div>`;
                    return quoteToHtml;
                }
            },
            selectedQuoteNum() {
                return this.selectedQuoteIndex + 1;
            },
            difficulty() {
                if (this.selectedQuoteIndex) {
                    let movieDifficulty = this.$store.getters.difficultySet[this.selectedQuoteIndex];
                    if (movieDifficulty !== '') {
                        return movieDifficulty;
                    }
                }

                return null;
            },
            difficultyObject() {
                if (this.difficulty === 'Hard') {
                    return {
                        color: 'red',
                        icon: 'gauge-full'
                    };
                }

                if (this.difficulty === 'Medium') {
                    return {
                        color: 'orange',
                        icon: 'gauge-low'
                    };
                }

                if (this.difficulty === 'Easy') {
                    return {
                        color: 'green',
                        icon: 'gauge-empty'
                    };
                }

                return {
                    color: 'light-grey',
                    icon: 'circle-off-outline',
                };
            }
        },

        methods: {
            async closeModal() {
                this.showDialogMovieAnswer = false;
                this.showHints = false;
                await this.randomizeSelectedQuote();
                this.$refs.guessAutocomplete.focus();
            },
            async randomizeSelectedQuote() {
                this.result = null;
                this.guess = null;
                this.hintYear = null;
                this.hintActor = null;
                this.title = null;
                this.imdbId = null;
                this.movieData = null;
                this.movieImgSrc = null;
                this.selectedQuoteIndex = Math.floor(Math.random() * this.quoteSet.length);

                await this.getMovieData();
            },
            getHintYear() {
                this.hintYear = this.$store.getters.yearSet[this.selectedQuoteIndex];
            },
            getHintActor() {
                this.hintActor = this.$store.getters.actorSet[this.selectedQuoteIndex];
            },
            getTitle() {
                this.title = this.$store.getters.titleSet[this.selectedQuoteIndex];
                return this.title;
            },
            async getMovieData() {
                this.imdbId = this.$store.getters.imdbSet[this.selectedQuoteIndex];

                if (this.selectedQuoteIndex && this.imdbId) {
                    await this.$store.dispatch('FETCH_MOVIE_DATA', this.imdbId);
                    this.movieData = await this.$store.getters.selectedMovieData;
                    this.movieImgSrc = this.movieData.Poster;
                }
            },
            async submitAnswer() {
                // CHECK THAT THE AUTOCOMPLETE MENU IS NOT OPEN BEFORE SUBMITTING THE ANSWER
                // so that you can @keypress "ENTER" in the autocomplete input to submit the answer
                if (this.$refs.guessAutocomplete.isMenuActive === true || !this.guess) return;

                let answer = await this.getTitle();

                if (answer === this.guess) {
                    this.result = true;
                } else {
                    this.result = false;
                }

                await this.getHintActor();
                await this.getHintYear();
                this.showDialogMovieAnswer = true;
                this.$refs.guessAutocomplete.isMenuActive = false;
            },
        }
    };
</script>
